<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
      <div class="relative overflow-x-auto shadow-md sm:rounded-lg mt-5">
        <table class="w-full text-left rtl:text-right text-gray-700">
          <thead>
            <tr class="bg-gray-100">
              <th scope="col" class="p-4">
                <div class="flex items-center">
                  <input
                    id="checkbox-all-search"
                    type="checkbox"
                    class="w-4 h-4 text-custom-yellow bg-gray-100 border-gray-300 rounded focus:ring-custom-yellow dark:focus:ring-custom-yellow dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label for="checkbox-all-search" class="sr-only">checkbox</label>
                </div>
              </th>
              <th scope="col" class="px-6">Request to</th>
              <th scope="col" class="px-6">Date requested</th>
              <th scope="col" class="px-6">Date required</th>
              <th scope="col" class="px-6">Quantity requested</th>
              <th scope="col" class="px-6">Status</th>
              <th scope="col" class="px-6">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(requisition_data, index) in facility_requests" :key="index" class="border-b">
              <td class="w-4 p-4">
                <div class="flex items-center">
                  <input :id="'checkbox-table-search-' + index" type="checkbox" class="w-4 h-4" />
                  <label :for="'checkbox-table-search-' + index" class="sr-only">checkbox</label>
                </div>
              </td>
              <td class="px-6 py-1">{{ requisition_data.request_to_facility?.name || 'N/A' }}</td>
              <td class="px-6 py-1">{{ formatDateISO(requisition_data.created_at) }}</td>
              <td class="px-6 py-1">{{ formatDateISO(requisition_data.date_required) }}</td>
              <td class="px-6 py-1">{{ requisition_data.ecosystem_products[0]?.requested_quantity || 'N/A' }}</td>
              <td class="px-6 py-1">
                <span 
                  class="inline-flex justify-center px-2.5 py-1 w-full rounded-md"
                  :class="{
                    'bg-green-100 text-green-800': requisition_data.status === 'Completed',
                    'bg-yellow-200 text-yellow-800': requisition_data.status === 'Pending',
                    'bg-blue-100 text-blue-800': requisition_data.status === 'Partially Issued',
                    'bg-red-100 text-red-800': requisition_data.status === 'Rejected',
                    'bg-gray-100 text-gray-800': !['Completed', 'Pending', 'Partially Issued', 'Rejected'].includes(requisition_data.status)
                  }"
                >
                  {{ requisition_data.status }}
                </span>
              </td>
              <td class="flex items-center px-4 py-2">
                <CustomButton
                  label="View Details"
                  @click="openDetailsModal(requisition_data)"
                  :disabled="isLoading[requisition_data.ecosystem_request_id]"
                  :bg-class="isLoading[requisition_data.ecosystem_request_id] ? 'bg-gray-300 cursor-not-allowed' : 'bg-custom-yellow py-1'"
                />
                <div
                  v-if="isLoading[requisition_data.ecosystem_request_id]"
                  class="ml-2 animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-900"
                ></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex justify-center gap-4 mt-4">
        <button
          :disabled="currentPage === 1 || pageLoading"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          @click="changePage(currentPage - 1)"
        >
          <svg
            class="w-6 h-6"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m15 19-7-7 7-7" />
          </svg>
          Previous
        </button>
        <div class="flex items-center gap-2">
          <button
            v-for="page in totalPages"
            :key="page"
            :class="[
              'relative h-10 max-h-[40px] w-10 max-w-[40px] select-none rounded-lg text-center align-middle font-medium transition-all',
              currentPage === page
                ? 'bg-custom-blue text-white shadow-md shadow-custom-blue/10 hover:shadow-lg hover:shadow-custom-blue/20'
                : 'text-custom-blue hover:bg-custom-blue/10 active:bg-custom-blue/20'
            ]"
            type="button"
            @click="changePage(page)"
          >
            <span class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
              {{ page }}
            </span>
          </button>
        </div>
        <button
          :disabled="currentPage === totalPages || pageLoading"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          @click="changePage(currentPage + 1)"
        >
          Next
          <svg
            class="w-6 h-6"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m9 5 7 7-7 7" />
          </svg>
        </button>
      </div>

      <!-- Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm"
        @click.self="closeModal"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
          <div class="bg-orange-200 text-gray-600 px-6 py-2 flex justify-between items-center">
            <h2 class="text-xl font-bold flex items-center">Request Details</h2>
            <button
              @click="closeModal"
              class="text-gray-700 hover:bg-custom-blue-dark p-1 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
          <div id="modalContent" class="p-6 overflow-y-auto" style="max-height: calc(85vh - 128px);">
            <div v-if="selectedRequest" class="space-y-6">
              <h2 class="text-xl font-bold text-gray-800">Request Details</h2>
              <div class="rounded-lg p-4 border border-gray-200">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div class="bg-white p-2">
                    <label class="text-sm text-gray-500 mb-1">Date Required</label>
                    <p class="text-base font-medium text-gray-900">{{ formatDateISO(selectedRequest.date_required) }}</p>
                  </div>
                  <div class="bg-white p-2">
                    <label class="text-sm text-gray-500 mb-1">Requesting Facility</label>
                    <p class="text-base font-medium text-gray-900">{{ selectedRequest.requesting_facility?.name || 'N/A' }}</p>
                  </div>
                  <div class="bg-white p-2">
                    <label class="text-sm text-gray-500 mb-1">Request To</label>
                    <p class="text-base font-medium text-gray-900">{{ selectedRequest.request_to_facility?.name || 'N/A' }}</p>
                  </div>
                  <div class="bg-white p-2">
                    <label class="text-sm text-gray-500 mb-1">Requested On</label>
                    <p class="text-base font-medium text-gray-900">{{ formatDateISO(selectedRequest.created_at) }}</p>
                  </div>
                  <div class="bg-white p-2">
                    <label class="text-sm text-gray-500 mb-2">Status</label>
                    <p 
                      class="px-1 py-1 w-20 text-xs font-medium flex justify-center rounded-md"
                      :class="{
                        'bg-green-100 text-green-800': selectedRequest.status === 'Completed',
                        'bg-yellow-200 text-yellow-800': selectedRequest.status === 'Pending',
                        'bg-blue-100 text-blue-800': selectedRequest.status === 'Partially Issued',
                        'bg-red-100 text-red-800': selectedRequest.status === 'Rejected',
                        'bg-gray-100 text-gray-800': !['Completed', 'Pending', 'Partially Issued', 'Rejected'].includes(selectedRequest.status)
                      }"
                    >
                      {{ selectedRequest.status }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-8">
                <h2 class="text-xl font-bold text-gray-800 mb-4">Units Requested</h2>
                <div class="relative overflow-x-auto mt-2 rounded-lg shadow-sm border border-gray-200">
                  <table class="w-full text-left rtl:text-right text-gray-700">
                    <thead>
                      <tr class="bg-gray-100 text-gray-800">
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Blood Group</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Unit Product</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Requested Quantity</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Issued Quantity</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Status</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white">
                      <tr
                        v-for="(product, index) in selectedRequest.ecosystem_products"
                        :key="index"
                        class="hover:bg-gray-100 transition-colors"
                      >
                        <td class="py-4 px-4 text-sm text-gray-900 border-b">{{ product.blood_group_description }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900 border-b">{{ product.unit_product_description }}</td>
                        <td class="px-4 py-3 text-sm font-medium">{{ product.requested_quantity }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900 border-b">
                          <span v-if="product.issued_quantity" class="font-medium">{{ product.issued_quantity }}</span>
                          <span v-else class="text-gray-500 italic">N/A</span>
                        </td>
                        <td class="py-4 px-4 text-sm text-gray-900 border-b">
                          <span 
                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            :class="{
                              'bg-green-100 text-green-800': product.status === 'Completed',
                              'bg-yellow-200 text-yellow-800': product.status === 'Pending',
                              'bg-blue-100 text-blue-800': product.status === 'Partially Issued',
                              'bg-red-100 text-red-800': product.status === 'Rejected',
                              'bg-gray-100 text-gray-800': !['Completed', 'Pending', 'Partially Issued', 'Rejected'].includes(product.status)
                            }"
                          >
                            {{ product.status }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="mt-8">
                <h2 class="text-xl font-bold text-gray-800 mb-4">Units Received</h2>
                <div class="relative overflow-x-auto mt-2 rounded-lg shadow-sm border border-gray-200">
                  <table class="w-full text-left rtl:text-right text-gray-700">
                    <thead>
                      <tr class="bg-gray-100 text-gray-800">
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Serial No.</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Blood Group</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Unit Product</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Status</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white">
                      <tr
                        v-for="log in flattenedLogs"
                        :key="`log-${log.productIndex}-${log.logIndex}`"
                        class="hover:bg-gray-100 transition-colors"
                      >
                        <td class="py-4 px-4 text-sm text-gray-900 border-b">{{ log.product?.pint_serial || 'N/A' }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900 border-b">{{ log.product?.blood_group?.description || 'N/A' }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900 border-b">{{ log.product?.unit_product?.description || 'N/A' }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900 border-b">
                          <span 
                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            :class="{
                              'bg-green-100 text-green-800': log.product?.status === 'Completed' || log.product?.status === 'Okay',
                              'bg-yellow-200 text-yellow-800': log.product?.status === 'Pending',
                              'bg-blue-100 text-blue-800': log.product?.status === 'Partially Issued',
                              'bg-red-100 text-red-800': log.product?.status === 'Rejected',
                              'bg-gray-100 text-gray-800': !['Completed', 'Pending', 'Partially Issued', 'Rejected', 'Okay'].includes(log.product?.status)
                            }"
                          >
                            {{ log.product?.status || 'N/A' }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <CustomButton
              label="Print"
              @click="printModalContent"
              bg-class="bg-custom-blue text-white text-sm py-2 px-6 rounded-md hover:bg-custom-blue-dark flex items-center"
            >
              <template #icon>
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z"></path>
                </svg>
              </template>
            </CustomButton>
            <CustomButton
              label="Close"
              @click="closeModal"
              bg-class="bg-orange-300 text-gray-700 text-sm py-2 px-6 rounded-md hover:bg-custom-blue-dark flex items-center"
            >
              <template #icon>
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </template>
            </CustomButton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import CustomButton from "@/components/CustomButton.vue";
import api from "@/services/api";

export default {
  name: "InitialRequests",
  components: {
    CustomButton,
  },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
    filters: {
      type: Object,
      default: () => ({
        blood_group: '',
        status: '',
        unit_product: '',
        start_date: '',
        end_date: '',
      }),
    },
  },
  data() {
    return {
      facility_requests: [],
      error: null,
      isLoading: {},
      loading: false,
      pageLoading: false,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      showModal: false,
      selectedRequest: null,
      facility_logo: '',
    };
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
      this.getFacilityRequest();
    },
    filters: {
      handler() {
        this.currentPage = 1;
        this.getFacilityRequest();
      },
      deep: true,
    },
  },
  created() {
    this.getFacilityRequest();
    this.user = this.$store.getters["auth/user"];
    this.facility_logo = this.user.user_roles[0]?.facility_department?.facility?.logo;
  },
  computed: {
    flattenedLogs() {
      return this.selectedRequest?.ecosystem_products?.flatMap((product, productIndex) =>
        product.ecosystem_issueout_logs.map((log, logIndex) => ({
          ...log,
          productIndex,
          logIndex,
        }))
      ) || [];
    },
  },
  methods: {
    async getFacilityRequest(page = 1) {
      this.pageLoading = true;
      this.loading = page === 1;
      try {
        const response = await api.getIndividualFacilityEcosystemBloodRequests({
          page,
          search_query: this.searchQuery,
          ...this.filters,
        });
        this.facility_requests = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.total_pages || 1;
        this.totalCount = response.total_count || 0;
      } catch (error) {
        this.error = error.message || "Failed to fetch facility requests";
        this.facility_requests = [];
      } finally {
        this.pageLoading = false;
        this.loading = false;
      }
    },
    openDetailsModal(request) {
      this.selectedRequest = request;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedRequest = null;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getFacilityRequest(page);
      }
    },
    formatDateISO(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    },
    printModalContent() {
      const requestData = JSON.parse(JSON.stringify(this.selectedRequest));
      const printWindow = document.createElement('iframe');
      printWindow.style.position = 'absolute';
      printWindow.style.width = '0';
      printWindow.style.height = '0';
      printWindow.style.border = 'none';
      document.body.appendChild(printWindow);

      try {
        const printContent = `
          <div class="page-container">
            <div class="report-container">
              <div class="logo-container">
                <img src="${this.facility_logo}" alt="Hospital Logo" class="logo">
              </div>
              <h1 class="text-lg font-bold mb-4">Blood Request Details</h1>
              <div class="section">
                <div class="section-title">General Information</div>
                <div class="grid">
                  <div>
                    <div class="info-row"><span class="info-label">Date Required:</span> ${this.formatDateISO(requestData.date_required)}</div>
                    <div class="info-row"><span class="info-label">Requesting Facility:</span> ${requestData.requesting_facility?.name || 'N/A'}</div>
                    <div class="info-row"><span class="info-label">Request To:</span> ${requestData.request_to_facility?.name || 'N/A'}</div>
                  </div>
                  <div>
                    <div class="info-row"><span class="info-label">Requested On:</span> ${this.formatDateISO(requestData.created_at)}</div>
                    <div class="info-row"><span class="info-label">Status:</span> ${requestData.status}</div>
                  </div>
                </div>
              </div>
              <div class="section">
                <div class="section-title">Units Requested</div>
                <div class="table-container">
                  <table class="w-full">
                    <thead>
                      <tr>
                        <th>Blood Group</th>
                        <th>Unit Product</th>
                        <th>Requested Quantity</th>
                        <th>Issued Quantity</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${requestData.ecosystem_products
                        .map(
                          (product, index) => `
                            <tr${index % 2 === 0 ? ' class="bg-gray-50"' : ''}>
                              <td>${product.blood_group_description}</td>
                              <td>${product.unit_product_description}</td>
                              <td>${product.requested_quantity}</td>
                              <td>${product.issued_quantity || 'N/A'}</td>
                              <td>${product.status}</td>
                            </tr>
                          `
                        )
                        .join('')}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        `;

        const printDocument = `
          <html>
          <head>
            <title>Blood Request Details</title>
            <style>
              @media print {
                @page {
                  size: A4;
                  margin: 2cm;
                }
              }
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #333;
                margin: 0;
                padding: 0;
              }
              .page-container {
                padding: 20px;
              }
              .logo-container {
                text-align: center;
                margin: 0 auto;
                display: flex;
                justify-content: center;
                margin-bottom: 15px;
              }
              .logo {
                width: 120px;
                align-items: center;
              }
              .report-container {
                max-width: 100%;
                margin: 0 auto;
              }
              h1 {
                text-align: center;
                font-size: 24px;
                margin-bottom: 5px;
              }
              .section {
                margin-bottom: 30px;
              }
              .section-title {
                font-size: 18px;
                font-weight: bold;
                border-bottom: 1px solid #ddd;
                padding-bottom: 8px;
                margin-bottom: 15px;
              }
              .grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 15px;
              }
              .info-row {
                margin-bottom: 8px;
              }
              .info-label {
                font-weight: bold;
              }
              .table-container {
                width: 100%;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 15px;
                font-size: 12px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px 12px;
                text-align: left;
              }
              th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              .bg-gray-50 {
                background-color: #f9f9f9;
              }
              @media print {
                body {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `;

        printWindow.contentDocument.open();
        printWindow.contentDocument.write(printDocument);
        printWindow.contentDocument.close();

        printWindow.contentWindow.onload = function () {
          printWindow.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(printWindow);
          }, 1000);
        };
      } catch (error) {
        console.error('Error generating print content:', error);
        document.body.removeChild(printWindow);
      }
    },
  },
};
</script>

<style lang="css" scoped>
/* No additional styles needed */
</style>